import { useCallback, useEffect } from 'react';

import { useAuthentication } from '@givebacks/api';
import { Location, offerKeys, useCreateMerchantActivation } from '@givebacks/api/offers';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useSelectHasEitherCardTypes } from 'core/Api';
import {
  MerchantDetailModal,
  MerchantDetailModalProvider,
  MerchantOfferTermsModal,
  useMerchantDetailModalProvider,
} from 'core/Components';
import { useBreakPoints } from 'core/Hooks';
import { getErrorMessage_v2 } from 'core/Utils';
import { useAtom, useSetAtom } from 'jotai';
import {
  Affix,
  Card,
  Center,
  Divider,
  Loader,
  LocalMerchantCard,
  ScrollArea,
  Stack,
  SwipeableContainer,
  notifications,
} from 'ui';
import { useInViewport } from 'ui/Hooks';

import { MerchantClickArgs } from '../InStoreMerchantMarkers';
import { openDetailModalAtom, storeMarkerModalAtom } from '../markerModalAtom';
import { useInStoreLocations } from '../useInStoreLocations';
import classes from './InStoreMerchantSearch.module.css';
import { EarnInStoreSearchHeader } from './InStoreMerchantSearchHeader';

type LocalMerchantResultsProps = {
  scrollAreaMah?: string;
};

const MerchantCard = ({
  location,
  withDivider = true,
  hasPartnerCard,
  onClick,
  onActivation,
  isAuthenticated,
}: {
  location: Location;
  withDivider?: boolean;
  hasPartnerCard?: boolean;
  onClick: (merchant: MerchantClickArgs) => void;
  onActivation?: () => void;
  isAuthenticated: boolean;
}) => {
  const isMerchantClaimed = location.merchant.dining || location.merchant.activation?.active;

  const handleClick = useCallback(() => {
    onClick({
      locationId: location.uuid,
      merchantId: location.merchant.uuid,
      type: location.merchant.in_store ? 'instore' : 'dining',
    });
  }, [location.merchant.in_store, location.merchant.uuid, location.uuid, onClick]);

  return (
    <Stack gap={0}>
      <LocalMerchantCard
        key={location.uuid}
        name={location.merchant.name}
        tagline={location.merchant.tagline}
        logoSrc={location.merchant.logo?.url}
        onTitleClick={handleClick}
        buttonProps={{
          onClick: onActivation,
          disabled: !hasPartnerCard,
        }}
        isClaimed={isMerchantClaimed}
        isLocked={!isAuthenticated || !hasPartnerCard}
      />
      {withDivider && <Divider />}
    </Stack>
  );
};

const LocalMerchantModals = () => {
  const [merchantInfo] = useAtom(storeMarkerModalAtom);
  const setBindOpenModal = useSetAtom(openDetailModalAtom);

  const { setMeta, openModalById } = useMerchantDetailModalProvider({});

  const { mutate: activateMerchant } = useCreateMerchantActivation();

  const queryClient = useQueryClient();
  const handleActivation = useCallback(
    (merchantId: string) => {
      if (!merchantId) throw new Error("Can't activate merchant without merchantId");

      activateMerchant(merchantId, {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: offerKeys.all });
        },
        onError: (e) => {
          const error = e as Error | AxiosError;
          notifications.show({
            message: getErrorMessage_v2(error),
            variant: 'error',
          });
        },
      });
    },
    [activateMerchant, queryClient]
  );

  useEffect(() => {
    if (merchantInfo) {
      setMeta({
        merchantId: merchantInfo?.merchantId,
        locationId: merchantInfo?.locationId,
        type: merchantInfo?.type,
      });
    }
  }, [merchantInfo, setMeta]);

  useEffect(() => {
    // Bind the 'open' fn to the atom
    const openDetail = () => {
      return () => openModalById('Detail');
    };

    setBindOpenModal(openDetail);
  }, [openModalById, setBindOpenModal]);

  return (
    <>
      <MerchantDetailModal handleClick={handleActivation} />
      <MerchantOfferTermsModal />
    </>
  );
};

const LocalMerchantResults = ({ scrollAreaMah }: LocalMerchantResultsProps) => {
  const queryClient = useQueryClient();
  const { ref, inViewport } = useInViewport();
  const { mutate: activateMerchant } = useCreateMerchantActivation();
  const { data: hasPartnerCard } = useSelectHasEitherCardTypes();
  const { isAuthenticated } = useAuthentication();

  const [openModal] = useAtom(openDetailModalAtom);
  const setModalMerchant = useSetAtom(storeMarkerModalAtom);

  const { locations, isLoading, isFetching, hasNextPage, fetchNextPage } = useInStoreLocations();

  const handleActivation = useCallback(
    (merchantId: string) => {
      if (!merchantId) throw new Error("Can't activate merchant without merchantId");

      activateMerchant(merchantId, {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: offerKeys.all });
        },
        onError: (e) => {
          const error = e as Error | AxiosError;
          notifications.show({
            message: getErrorMessage_v2(error),
            variant: 'error',
          });
        },
      });
    },
    [activateMerchant, queryClient]
  );

  const handleMerchantCLick = useCallback(
    (merchant: MerchantClickArgs) => {
      setModalMerchant({ ...merchant });
      if (!openModal) return;
      return openModal();
    },
    [openModal, setModalMerchant]
  );

  useEffect(() => {
    if (inViewport && hasNextPage) {
      void fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, inViewport]);

  return (
    <>
      <ScrollArea.Autosize
        mah={scrollAreaMah ? scrollAreaMah : undefined}
        style={{ overflowX: 'hidden' }}
      >
        <Stack gap={0} h="100%">
          <Divider />
          {locations?.map((location) => (
            <MerchantCard
              key={location.uuid}
              location={location}
              hasPartnerCard={hasPartnerCard}
              onClick={handleMerchantCLick}
              onActivation={() => handleActivation(location.merchant.uuid)}
              isAuthenticated={isAuthenticated}
            />
          ))}
        </Stack>
        <Center p={'md'} ref={ref}>
          {(isLoading || isFetching) && <Loader />}
        </Center>
      </ScrollArea.Autosize>
      <MerchantDetailModalProvider meta={{}}>
        <LocalMerchantModals />
      </MerchantDetailModalProvider>
    </>
  );
};

const LocalMerchantSearchContent = ({ scrollAreaMah }: LocalMerchantResultsProps) => {
  return (
    <Stack h="100%">
      <EarnInStoreSearchHeader />
      <LocalMerchantResults scrollAreaMah={scrollAreaMah} />
    </Stack>
  );
};

const LocalMerchantSearchCard = ({ scrollAreaMah }: LocalMerchantResultsProps) => {
  return (
    <Card className={classes.card}>
      <LocalMerchantSearchContent scrollAreaMah={scrollAreaMah} />
    </Card>
  );
};

const LocalMerchantSearch = () => {
  const { isMediumScreen } = useBreakPoints();

  if (isMediumScreen) {
    return (
      <SwipeableContainer
        defaultHeight={135}
        openY={60}
        bounds={{ bottom: 55, top: 5 }}
        threshold={{ upper: 55, lower: 30 }}
      >
        <LocalMerchantSearchContent />
      </SwipeableContainer>
    );
  }
  return (
    <Affix
      className={classes.earnAffix}
      position={{
        right: 24,
        // extra padding to account for the control buttons
        top: 'calc(var(--app-shell-header-height) + var(--app-shell-padding) + 45px)',
      }}
    >
      {/* This manual calculation will probably need to be converted to actual logic */}
      <LocalMerchantSearchCard scrollAreaMah="calc(80dvh - 350px)" />
    </Affix>
  );
};

export { LocalMerchantSearch };
